import { network } from '@utils'
import { handleReturn, MutationFunction, QueryFunction } from './root'

export interface IReferralMyLink {
  code: string
  link: string
  total_referrals: number
  rewarded_count: number
  available_count: number
  redeemed_count: number
}

export interface IReferralCapturePayload {
  code: string
}

export interface IReferralRedeemPayload {
  reward_id: number
  business_slug: string
}

export interface IReferralRedeemResponse {
  reward_id: number
  status: string
  business_slug: string
  redeemed_at: string | null
  reward_role: string
}

export interface IReferralReward {
  reward_id: number
  reward_role: 'referrer' | 'referee'
  reward_type: string
  credit_months: number
  available_at: string
  counterpart_name: string
}

export class ReferralCaptureError extends Error {
  code: 'invalid_code' | 'capture_failed'

  constructor(message: string, code: ReferralCaptureError['code']) {
    super(message)
    this.name = 'ReferralCaptureError'
    this.code = code
  }
}

export const fetchReferralMyLink: QueryFunction<void, IReferralMyLink> = async () => {
  const url = '/v1/referral/my-link/'
  const { data } = await network.httpClient.request({
    url,
    method: 'GET'
  })
  return data
}

export const fetchReferralRewards: QueryFunction<void, IReferralReward[]> = async () => {
  const url = '/v1/referral/rewards/'
  const { data } = await network.httpClient.request({
    url,
    method: 'GET'
  })
  return data
}

export const captureReferralCode: MutationFunction<void, IReferralCapturePayload> = async (
  _,
  requestData
) => {
  const code = requestData?.code?.trim()
  if (!code) {
    throw new ReferralCaptureError('Referral code is required', 'capture_failed')
  }

  const url = '/v1/referral/capture/'
  const { status } = await network.httpClient.request({
    url,
    method: 'POST',
    data: { code },
    validateStatus: () => true
  })

  if (status === 404) {
    throw new ReferralCaptureError('Invalid referral code', 'invalid_code')
  }

  if (status !== 204) {
    throw new ReferralCaptureError('Failed to capture referral code', 'capture_failed')
  }

  return { code }
}

export const redeemReferralReward: MutationFunction<void, IReferralRedeemPayload> = async (
  _,
  requestData
) => {
  const url = '/v1/referral/redeem/'
  const { status, data } = await network.httpClient.request({
    url,
    method: 'POST',
    data: requestData,
    validateStatus: () => true
  })
  return handleReturn({ status, data }) as IReferralRedeemResponse
}
